import {
  createContext,
  Dispatch,
  MutableRefObject,
  PropsWithChildren,
  SetStateAction,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import identity from "lodash/identity";
import uniqBy from "lodash/uniqBy";
import noop from "lodash/noop";

import { useApi } from "shared/lib/hooks/use-api";
import {
  AccountStatusI,
  SchedulingConfigI,
} from "shared/lib/interfaces/account";

import {
  APII,
  GetKanbanAccountsResponseI,
  glencocoClientAPI as apiFactory,
  UpdateKanbanAccountParamsI,
  AddLeadToAccountParamsI,
} from "@/api/glencoco";
import { ERRORS } from "@/constants/errors";
import { useCampaigns } from "@/hooks/use-campaigns";

import { swallow } from "@/api/legacy-swallow";

export interface ModalAccountDataI {
  campaignId: string;
  accountId: string;
  accountName: string;
  accountStatus: AccountStatusI;
  targetAccountStatus?: AccountStatusI;
  cancelMeetingRequestNote?: string;
  contact?: {
    contact_id?: string;
    title: string;
    name: string;
    email?: string;
    person_city?: string;
    person_state?: string;
    person_postal_code?: string;
  };
  followUpTime?: string;
  accountExecutive?: {
    id: string;
    first_name: string;
    last_name: string;
    scheduling_link: string;
  };
  scheduling?: SchedulingConfigI;
}

export interface ModalAccountContactI {
  first_name: string;
  last_name: string;
  title: string;
  phone: string;
  email: string;
}

type StatusTransitionActionRefI = MutableRefObject<null | (() => any)>;

export interface AccountsContextI {
  selectedCampaignIds: string[];
  setSelectedCampaignIds: Dispatch<SetStateAction<string[]>>;
  isLoadingAccounts: boolean;
  isUpdatingAccounts: boolean;
  modalAccount: ModalAccountDataI | null;
  setModalAccount: Dispatch<SetStateAction<ModalAccountDataI | null>>;
  modalContact: ModalAccountContactI | null;
  setModalContact: Dispatch<SetStateAction<ModalAccountContactI | null>>;
  isAccountUpdating: boolean;
  updateAccountWithCampaign: (
    accountId: string,
    campaignId: string,
    props: UpdateKanbanAccountParamsI
  ) => Promise<any>;
  addLeadToAccount: (
    accountId: string,
    campaignId: string,
    props: AddLeadToAccountParamsI
  ) => Promise<any>;
  loadAccounts: () => Promise<any>;
  openAccountsData: GetKanbanAccountsResponseI | null;
  clearModalAccount: () => void;
  clearModalContact: () => void;
  onChangeStatusSuccessRef: StatusTransitionActionRefI;
  onChangeStatusCancelRef: StatusTransitionActionRefI;
  toggleDoNotCall: (
    accountId: string,
    campaignId: string,
    note: string
  ) => Promise<any>;
}

export const AccountsContext = createContext<AccountsContextI>({
  selectedCampaignIds: [],
  setSelectedCampaignIds: identity,
  isLoadingAccounts: false,
  setModalAccount: identity,
  setModalContact: identity,
  isAccountUpdating: false,
  isUpdatingAccounts: false,
  updateAccountWithCampaign: identity,
  addLeadToAccount: identity,
  loadAccounts: () => Promise.resolve(),
  toggleDoNotCall: () => Promise.resolve(),
  modalAccount: null,
  modalContact: null,
  clearModalAccount: noop,
  clearModalContact: noop,
  openAccountsData: null,
  onChangeStatusSuccessRef: { current: null },
  onChangeStatusCancelRef: { current: null },
});

const updateAccountWithCampaignFetcher = (
  api: APII,
  accountId: string,
  campaignId: string,
  params: UpdateKanbanAccountParamsI
) => swallow(api.updateAccountWithCampaign(accountId, campaignId, params));

const addLeadToAccountFetcher = (
  api: APII,
  accountId: string,
  campaignId: string,
  params: AddLeadToAccountParamsI
) => swallow(api.addLeadToAccount(campaignId, accountId, params));

const toggleDoNotCallFetcher = (
  api: APII,
  accountId: string,
  campaignId: string,
  note: string
) => swallow(api.toggleDoNotCall(accountId, campaignId, note));

export const AccountsContextProvider = ({ children }: PropsWithChildren) => {
  const { refillCampaignsByIds } = useCampaigns();

  const onChangeStatusSuccessRef: StatusTransitionActionRefI = useRef(null);
  const onChangeStatusCancelRef: StatusTransitionActionRefI = useRef(null);

  const [selectedCampaignIds, setSelectedCampaignIds] = useState<string[]>([]);
  const [modalAccount, setModalAccount] = useState<ModalAccountDataI | null>(
    null
  );

  const clearModalAccount = () => setModalAccount(null);

  const [modalContact, setModalContact] = useState<ModalAccountContactI | null>(
    null
  );
  const clearModalContact = () => setModalContact(null);

  const getAccountsFetcher = useMemo(() => {
    return (api: APII) => {
      return swallow(api.getKanbanAccounts({ campaigns: selectedCampaignIds }));
    };
  }, [selectedCampaignIds]);

  const [
    { isLoading: isLoadingAccounts, data: openAccountsData },
    loadAccounts,
  ] = useApi({
    fetcher: getAccountsFetcher,
    isInitiallyLoading: false,
    shouldCallAutomatically: false,
    shouldResetDataOnRequest: false,
    apiFactory,
  });

  const isUpdatingAccounts = useMemo(
    () => !!openAccountsData && isLoadingAccounts,
    [isLoadingAccounts, openAccountsData]
  );

  const [
    { isLoading: isAccountWithCampaignUpdating },
    updateAccountWithCampaign,
  ] = useApi({
    apiFactory,
    fetcher: updateAccountWithCampaignFetcher,
    shouldCallAutomatically: false,
    errorBuilder: (resp) =>
      ERRORS[(resp as any)?.response?.data?.error_code as number] ||
      "Failed to update account status",
  });

  const [, toggleDoNotCall] = useApi({
    fetcher: toggleDoNotCallFetcher,
    apiFactory,
    errorBuilder: () => "Failed to toggle do not call",
    shouldCallAutomatically: false,
  });

  const [, addLeadToAccount] = useApi({
    fetcher: addLeadToAccountFetcher,
    apiFactory,
    errorBuilder: () => "Failed to add lead to account",
    shouldCallAutomatically: false,
  });

  useEffect(() => {
    const openAccountUniqueCampaignIds = uniqBy(
      openAccountsData?.items,
      (account) => account?.campaign_id
    ).map((account) => account.campaign_id);

    if (openAccountUniqueCampaignIds?.length) {
      refillCampaignsByIds(openAccountUniqueCampaignIds);
    }
  }, [openAccountsData?.items]);

  return (
    <AccountsContext.Provider
      value={{
        selectedCampaignIds,
        setSelectedCampaignIds,
        isLoadingAccounts,
        isUpdatingAccounts,
        openAccountsData,
        setModalAccount,
        setModalContact,
        loadAccounts,
        isAccountUpdating: isAccountWithCampaignUpdating,
        updateAccountWithCampaign,
        addLeadToAccount,
        modalAccount,
        clearModalAccount,
        modalContact,
        clearModalContact,
        toggleDoNotCall,
        onChangeStatusSuccessRef,
        onChangeStatusCancelRef,
      }}
    >
      {children}
    </AccountsContext.Provider>
  );
};
